import { useEffect, useMemo } from "react"
import { formatDate } from "@sembark-travel/datetime-utils"
import { Helmet } from "react-helmet-async"
import { SWRConfig, SWRConfiguration } from "swr"
import {
	Box,
	Container,
	Heading,
	Icons,
	Inline,
	RelativeTime,
	Spinner,
	Stack,
	Table,
	Text,
	Tooltip,
} from "@sembark-travel/ui-base"
import { ListView, TSearchParams, useSearch } from "@sembark-travel/ui-list"
import { Link, useLocationQuery } from "@sembark-travel/ui-router"
import {
	extendXHRInstance,
	useXHR,
	XHRContext,
	type XHRProps,
} from "@sembark-travel/xhr"
import Logo from "../images/Logo"
import { CodeChip } from "../HotelOtaMealPlans"
import { generatePath, useNavigate } from "../router-utils"
import { RateSheetView, VersionHistoryChip } from "./Item"
import {
	sharedRateSheetsXHR,
	TRateSheetSeason,
	TSharedRateSheet,
	TSharedRateSheetHead,
	useIsMobile,
	useSharedRateSheet,
} from "./store"
import {
	formatSeasonsMonthRange,
	getBinaryToDays,
	pluralize,
	shareTokenSearch,
	withShareToken,
} from "./utils"

// The shared pages are opened by partners who are not signed in. The app wide
// SWR config pauses every request until someone signs in, and its cache is
// shared with the signed in screens (which use the same keys), so this subtree
// gets its own cache and never pauses.
const sharedSWRConfig: SWRConfiguration = {
	isPaused: () => false,
	provider: () => new Map(),
}

/**
 * Wraps the pages a partner reaches through a shared rate sheet link. Every
 * request made below it carries the link's token, which is what lets the
 * existing rate sheet screens load without a session.
 */
export function SharedRateSheetsLayout({
	guestToken,
	children,
}: {
	guestToken: string
	children: React.ReactNode
}) {
	const xhr = useXHR()
	// Set as a default param rather than an interceptor, because the list
	// screens derive their own instances from this one and only the defaults
	// are carried over. The cast is because the context is typed as the axios
	// static, not an instance.
	const sharedXHR = useMemo(
		() =>
			extendXHRInstance(xhr, {
				params: { guest_token: guestToken },
			}) as unknown as XHRProps["xhr"],
		[xhr, guestToken]
	)

	return (
		<>
			<Helmet>
				<meta name="robots" content="noindex" />
				{/* The token rides in the url, so keep it out of the Referer header */}
				<meta name="referrer" content="no-referrer" />
			</Helmet>
			<SharedHeader />
			{guestToken ? (
				<SWRConfig value={sharedSWRConfig}>
					<XHRContext.Provider value={sharedXHR}>
						{children}
					</XHRContext.Provider>
				</SWRConfig>
			) : (
				<Container paddingY="12">
					<Stack gap="2" alignItems="center" textAlign="center">
						<Heading as="h3">Invalid link</Heading>
						<Text color="muted">
							This link is incomplete. Please open the rate sheet from the email
							you received.
						</Text>
					</Stack>
				</Container>
			)}
		</>
	)
}

function SharedHeader() {
	return (
		<Box as="header" position="relative" zIndex="50">
			<Box
				bgColor="black"
				color="white"
				className="app-header"
				position="relative"
				zIndex="10"
			>
				<Container fluid>
					<Inline alignItems="center" style={{ height: "3.5rem" }}>
						<Logo width="32" height="32" />
					</Inline>
				</Container>
			</Box>
		</Box>
	)
}

// The list is only paged, so nothing else is read from the url or sent to the api
type TSharedParams = Pick<TSearchParams, "page" | "limit">
type TSharedQuery = TSharedParams & { guest_token?: string }

// The token lives in the url next to the list params, but is not one of them
function queryWithoutToken(query: TSharedQuery): TSharedParams {
	const params = { ...query }
	delete params.guest_token
	return params
}

/**
 * The rate sheets hotel groups have shared with the partner the link's token
 * belongs to
 */
export function SharedRateSheetsList({ guestToken }: { guestToken: string }) {
	const [query, setQuery] = useLocationQuery<TSharedParams, TSharedQuery>({
		// put the token back, so paging keeps the link working
		toQuery: (params) => ({ ...params, guest_token: guestToken }),
		fromQuery: queryWithoutToken,
	})
	const [params, setParams] = useSearch<TSharedParams>(query)

	useEffect(() => {
		setQuery(params)
	}, [setQuery, params])

	return (
		<Stack>
			<Box paddingX={{ xs: "4", xl: "8" }} paddingY="3" borderBottomWidth="1">
				<Heading as="h2" fontSize="lg">
					B2B Rate Sheets
				</Heading>
			</Box>
			<ListView<TSharedRateSheetHead, TSharedParams>
				pageKey="shared-rate-sheets"
				params={params}
				fetch={(xhr, params) => sharedRateSheetsXHR(xhr).get(params)}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items }) => (
					<Table
						responsive
						bordered
						headers={[
							"Hotel Group",
							"Seasons",
							"No of Hotels",
							"Last Updated on",
						]}
						rows={items.map(
							({ rate_sheet: rateSheet, id, tenant_name: tenantName }) => [
								// The version that was shared, not the head's latest
								<Link
									to={withShareToken(
										generatePath("/shared/rate-sheets/:rateSheetId", {
											rateSheetId: String(rateSheet.id),
										}),
										guestToken
									)}
									anchored
									style={{ display: "inline-block" }}
								>
									<Inline alignItems="center" gap="2">
										<Text color="primary" fontWeight="semibold">
											{tenantName}
										</Text>
										<CodeChip code={String(id)} />
									</Inline>
								</Link>,
								<SeasonsRange seasons={rateSheet.seasons} />,
								<Text>{rateSheet.hotels.length}</Text>,
								<Text color="muted">
									<RelativeTime timestamp={rateSheet.created_at} />
								</Text>,
							]
						)}
					/>
				)}
			</ListView>
		</Stack>
	)
}

// The months the sheet's seasons span, each season and its intervals on hover
function SeasonsRange({ seasons }: { seasons: TRateSheetSeason[] }) {
	const range = formatSeasonsMonthRange(seasons)
	if (!range) return <Text color="muted">-</Text>
	return (
		<Tooltip
			placement="bottom-start"
			content={<SeasonsTooltipContent seasons={seasons} />}
		>
			<Text as="span" whiteSpace="nowrap">
				{range}
			</Text>
		</Tooltip>
	)
}

function SeasonsTooltipContent({ seasons }: { seasons: TRateSheetSeason[] }) {
	const intervalsCount = seasons.reduce(
		(count, season) => count + season.intervals.length,
		0
	)
	return (
		<Stack paddingY="1">
			<Box paddingBottom="2">
				<Text fontWeight="semibold">
					{pluralize(seasons.length, "Season")} •{" "}
					{pluralize(intervalsCount, "Interval")}
				</Text>
			</Box>
			{seasons.map((season) => (
				<Stack
					key={season.id}
					gap="2"
					paddingY="2"
					borderTopWidth="1"
					borderColor="emphasis"
				>
					<Text fontWeight="semibold">{season.name}</Text>
					{season.intervals.map((interval) => {
						const days = getBinaryToDays(interval.weekdays_bitmap_str)
						return (
							<Inline
								key={interval.id}
								gap="4"
								alignItems="center"
								justifyContent="between"
							>
								<Inline gap="1" alignItems="center">
									<Icons.CalendarDays size="3" />
									<Text whiteSpace="nowrap">
										{formatDate(interval.from, "DD MMM, YYYY")} &rarr;{" "}
										{formatDate(interval.to, "DD MMM, YYYY")}
									</Text>
								</Inline>
								{days.length === 7 ? (
									<Text
										fontSize="xs"
										whiteSpace="nowrap"
										style={{ opacity: 0.7 }}
									>
										All 7 Days
									</Text>
								) : (
									<Inline gap="1" alignItems="center">
										{days.map((day) => (
											<Inline
												key={day}
												size="4"
												alignItems="center"
												justifyContent="center"
												rounded="full"
												borderWidth="1"
												// The tooltip's own text colour, in either theme
												style={{ borderColor: "currentColor" }}
												title={day}
											>
												<Text style={{ fontSize: "10px", lineHeight: "1" }}>
													{day[0]}
												</Text>
											</Inline>
										))}
									</Inline>
								)}
							</Inline>
						)
					})}
				</Stack>
			))}
		</Stack>
	)
}

/**
 * The version of a rate sheet that was shared with the partner. Read by the
 * version's own id, so a newer version of the sheet stays out of view until it
 * is shared.
 */
export function SharedRateSheetDetail({
	id,
	guestToken,
}: {
	id: number
	guestToken: string
}) {
	const { data: rateSheet, isLoading, error } = useSharedRateSheet(id)

	if (isLoading) {
		return (
			<Box padding="6" textAlign="center">
				<Spinner />
			</Box>
		)
	}

	if (error || !rateSheet) {
		return (
			<Box padding="6" textAlign="center">
				<Text color="danger">Failed to load the rate sheet.</Text>
			</Box>
		)
	}

	return (
		<Stack>
			<SharedRateSheetDetailHeader
				rateSheet={rateSheet}
				guestToken={guestToken}
			/>
			<RateSheetView rateSheet={rateSheet} />
		</Stack>
	)
}

function SharedRateSheetDetailHeader({
	rateSheet,
	guestToken,
}: {
	rateSheet: TSharedRateSheet
	guestToken: string
}) {
	const navigate = useNavigate()
	const isMobile = useIsMobile()

	return (
		<Inline
			gap="3"
			alignItems="center"
			bgColor="subtle"
			paddingY="3"
			paddingX={isMobile ? "3" : "6"}
			borderBottomWidth="1"
		>
			<Box
				cursor="pointer"
				display="flex"
				alignItems="center"
				onClick={() =>
					navigate({
						pathname: "/shared/rate-sheets",
						search: shareTokenSearch(guestToken),
					})
				}
				title="Back to Rate Sheets"
			>
				<Icons.ArrowLeft size="5" />
			</Box>
			<Heading as="h3" fontSize="md">
				{rateSheet.tenant_name}
			</Heading>
			<CodeChip code={String(rateSheet.rate_sheet_head_id)} />
			<VersionHistoryChip rateSheet={rateSheet} api={sharedRateSheetsXHR} />
		</Inline>
	)
}
